import { GoogleIcon } from '@/components/auth/GoogleSignInButton';
import { OAuthCallbackHandler } from '@/components/auth/OAuthCallbackHandler';
import { Card } from '@/components/ui';
import { Alert } from '@/components/ui/alert';
import { Field } from '@/components/ui/field';
import { toaster } from '@/components/ui/toaster';
import { useAuth } from '@/contexts/auth-context';
import { resetProductAnalytics } from '@/libs/product-analytics';
import {
  useAcceptTeamInvitation,
  type AcceptTeamInvitationResponse,
} from '@/hooks/actions/use-accept-team-invitation';
import { useTeamInvitationDetails } from '@/hooks/queries/use-team-invitation-details';
import { authConfig } from '@/libs/auth-config';
import { getOAuthErrorMessage, isOAuthCallback } from '@/libs/oauth-utils';
import { tokenManager } from '@/libs/token-manager';
import {
  Badge,
  Box,
  Button,
  Container,
  Flex,
  Heading,
  HStack,
  Input,
  Link,
  Separator,
  Spinner,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Logo } from '@fluenceur/ui';
import { ErrorCode } from '@fluenceur/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';

/**
 * Maps backend error codes to translation keys
 */
function getErrorTranslationKey(code?: string): string {
  switch (code) {
    case ErrorCode.E_PLAN_TEAM_MEMBERS_LOCKED:
      return 'errors.E_PLAN_TEAM_MEMBERS_LOCKED';
    case 'E_TEAM_INVITATION_NOT_FOUND':
      return 'team.join.errors.invalidToken';
    case 'E_TEAM_INVITATION_EXPIRED':
      return 'team.join.errors.expired';
    case 'E_TEAM_INVITATION_CANCELLED':
      return 'team.join.errors.cancelled';
    case 'E_TEAM_INVITATION_ALREADY_ACCEPTED':
      return 'team.join.errors.alreadyAccepted';
    case 'E_EMAIL_MISMATCH':
      return 'team.join.errors.emailMismatch';
    case 'E_USER_ALREADY_MEMBER':
      return 'team.join.errors.alreadyMember';
    default:
      return 'team.join.errors.genericError';
  }
}

const JoinTeamPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { user, isAuthenticated, isLoading: isAuthLoading, logout, refreshCurrentUser } = useAuth();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Detect OAuth callback - tokens are in the URL after Google OAuth redirect
  const isOAuthRedirect = isOAuthCallback(searchParams);

  // Don't fetch invitation details during OAuth callback - the invitation was already
  // accepted by the backend, so this query would return "already accepted" error
  const {
    data: invitation,
    isLoading,
    error: queryError,
  } = useTeamInvitationDetails(isOAuthRedirect ? null : token);
  const acceptMutation = useAcceptTeamInvitation();

  // Zod schema for signup form
  const signupSchema = useMemo(
    () =>
      z.object({
        firstName: z.string().min(2, t('team.join.validation.firstNameRequired')),
        lastName: z.string().min(2, t('team.join.validation.lastNameRequired')),
        password: z.string().min(8, t('team.join.validation.passwordTooShort')),
      }),
    [t]
  );

  type SignupFormData = z.infer<typeof signupSchema>;

  const {
    register,
    handleSubmit,
    formState: { errors: formErrors, isSubmitting },
  } = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      password: '',
    },
  });

  // Handle OAuth error from URL parameters
  useEffect(() => {
    const error = searchParams.get('error');
    if (error) {
      setErrorMessage(
        error === ErrorCode.E_PLAN_TEAM_MEMBERS_LOCKED
          ? t(getErrorTranslationKey(error))
          : getOAuthErrorMessage(error)
      );
    }
  }, [searchParams, t]);

  // Clear stale tokens for unauthenticated users to prevent auth interference
  // Skip during OAuth redirect - OAuthCallbackHandler needs to process the tokens first
  useEffect(() => {
    if (!isAuthLoading && !isAuthenticated && !isOAuthRedirect) {
      tokenManager.clearTokens();
    }
  }, [isAuthLoading, isAuthenticated, isOAuthRedirect]);

  // No token provided
  if (!token) {
    return (
      <PageLayout>
        <ErrorCard message={t('team.join.errors.noToken')} />
      </PageLayout>
    );
  }

  // OAuth callback - render handler with loading state while tokens are processed
  if (isOAuthRedirect) {
    return (
      <PageLayout>
        <OAuthCallbackHandler />
        <Card.Root bg="white">
          <Card.Body>
            <VStack py={8} gap={4}>
              <Spinner size="lg" />
              <Text color="fg.muted">{t('common.loading')}</Text>
            </VStack>
          </Card.Body>
        </Card.Root>
      </PageLayout>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <PageLayout>
        <Card.Root bg="white">
          <Card.Body>
            <VStack py={8} gap={4}>
              <Spinner size="lg" />
              <Text color="fg.muted">{t('common.loading')}</Text>
            </VStack>
          </Card.Body>
        </Card.Root>
      </PageLayout>
    );
  }

  // Query error
  if (queryError) {
    const axiosError = queryError as { response?: { data?: { code?: string } } };
    const code = axiosError?.response?.data?.code;
    const translationKey = getErrorTranslationKey(code);
    return (
      <PageLayout>
        <ErrorCard message={t(translationKey)} />
      </PageLayout>
    );
  }

  // No data (shouldn't happen if no error, but defensive)
  if (!invitation) {
    return (
      <PageLayout>
        <ErrorCard message={t('team.join.errors.genericError')} />
      </PageLayout>
    );
  }

  // Determine which scenario we're in
  const isEmailMatch = isAuthenticated && user?.email === invitation.email;
  const isEmailMismatch = isAuthenticated && user?.email !== invitation.email;

  // Handle accept for authenticated users
  const handleAccept = async () => {
    setErrorMessage(null);
    try {
      await acceptMutation.mutateAsync({ token });
      await refreshCurrentUser();
      toaster.success({
        title: t('team.join.success.title'),
        description: t('team.join.success.description'),
      });
      navigate('/dashboard', { replace: true });
    } catch (error: unknown) {
      const axiosErr = error as { response?: { data?: { code?: string } } };
      setErrorMessage(t(getErrorTranslationKey(axiosErr?.response?.data?.code)));
    }
  };

  // Handle signup form submission
  const onSignupSubmit = async (data: SignupFormData) => {
    setErrorMessage(null);
    try {
      const result: AcceptTeamInvitationResponse = await acceptMutation.mutateAsync({
        token,
        password: data.password,
        firstName: data.firstName,
        lastName: data.lastName,
      });

      if ('requiresLogin' in result && result.requiresLogin) {
        navigate(`/?returnTo=${encodeURIComponent(`/team/join?token=${token}`)}`);
        return;
      }

      // Store tokens from accept response directly
      if ('token' in result && result.token) {
        resetProductAnalytics();
        tokenManager.setTokens({
          accessToken: result.token.accessToken,
          refreshToken: result.token.refreshToken,
          type: 'Bearer',
          expiresIn: result.token.expiresIn,
        });
      }

      await refreshCurrentUser();
      toaster.success({
        title: t('team.join.success.title'),
        description: t('team.join.success.description'),
      });
      navigate('/dashboard', { replace: true });
    } catch (error: unknown) {
      const axiosErr = error as { response?: { data?: { code?: string } } };
      setErrorMessage(t(getErrorTranslationKey(axiosErr?.response?.data?.code)));
    }
  };

  // Handle "Log in to join" button
  const handleLoginRedirect = () => {
    navigate(`/?returnTo=${encodeURIComponent(`/team/join?token=${token}`)}`);
  };

  // Handle sign out
  const handleSignOut = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
      // Stay on the same page - will now show the correct scenario
    } finally {
      setIsLoggingOut(false);
    }
  };

  // Handle Google OAuth for team invitation
  const handleGoogleSignup = () => {
    setIsGoogleLoading(true);
    const redirectUrl = `${window.location.origin}/team/join?token=${token}`;
    const googleAuthUrl = `${authConfig.getApiUrl()}/auth/google/redirect?mode=team_invitation&invite_token=${token}&redirect_url=${encodeURIComponent(redirectUrl)}`;
    window.location.href = googleAuthUrl;
  };

  return (
    <PageLayout>
      <OAuthCallbackHandler />

      <Card.Root bg="white">
        <Card.Body>
          <Stack gap={4} width="full">
            {/* Invitation header */}
            <VStack gap={1}>
              <Heading as="h1" size="xl" fontFamily="display" color="dark" textAlign="center">
                {t('team.join.title', { organizationName: invitation.organizationName })}
              </Heading>
              <Text color="fg.muted" textAlign="center">
                {t('team.join.subtitle', { inviterName: invitation.inviterName })}
              </Text>
            </VStack>

            {/* Brand accesses */}
            {invitation.brandAccesses.length > 0 ? (
              <Box>
                <Text fontSize="sm" fontWeight="medium" color="dark" mb={2}>
                  {t('team.join.brandAccessTitle')}
                </Text>
                <VStack gap={2} align="stretch">
                  {invitation.brandAccesses.map((access) => (
                    <HStack
                      key={access.brandName}
                      justify="space-between"
                      px={3}
                      py={2}
                      bg="gray.50"
                      borderRadius="md"
                    >
                      <Text fontSize="sm" color="dark">
                        {access.brandName}
                      </Text>
                      <Badge size="sm" variant="subtle" colorPalette="gray">
                        {t(`settings.team.roles.${access.role}`)}
                      </Badge>
                    </HStack>
                  ))}
                </VStack>
              </Box>
            ) : null}

            {/* Error message */}
            {errorMessage ? (
              <Alert status="error" title={t('common.error')}>
                {errorMessage}
              </Alert>
            ) : null}

            {/* Scenario A: Authenticated + email match */}
            {isEmailMatch ? (
              <Button
                colorPalette="gray"
                size="lg"
                width="full"
                h={12}
                onClick={handleAccept}
                loading={acceptMutation.isPending}
                loadingText={t('team.join.joining')}
              >
                {t('team.join.joinButton')}
              </Button>
            ) : isEmailMismatch ? (
              /* Scenario: Authenticated but email mismatch */
              <VStack gap={3}>
                <Alert status="warning" title={t('team.join.errors.emailMismatch')} />
                <Button
                  variant="outline"
                  size="lg"
                  width="full"
                  h={12}
                  onClick={handleSignOut}
                  loading={isLoggingOut}
                  loadingText={t('team.join.signingOut')}
                >
                  {t('team.join.signOut')}
                </Button>
              </VStack>
            ) : invitation.userExists ? (
              /* Scenario B: Existing user, not authenticated */
              <VStack gap={3} width="full">
                <Text fontSize="sm" color="fg.muted" textAlign="center">
                  {t('team.join.loginToJoinDescription')}
                </Text>
                <Button
                  colorPalette="gray"
                  size="lg"
                  width="full"
                  h={12}
                  onClick={handleLoginRedirect}
                >
                  {t('team.join.loginToJoin')}
                </Button>

                <OrSeparator />

                {/* The existing account may carry no organization yet (signup
                    abandoned before the company step). Logging in cannot help
                    it: the dashboard session bootstrap refuses an org-less
                    brand user, so the login page is a dead end. This flow
                    attaches the organization while signing the user in, which
                    makes it the only exit for those accounts. */}
                <ContinueWithGoogleButton
                  onClick={handleGoogleSignup}
                  isLoading={isGoogleLoading}
                />
              </VStack>
            ) : (
              /* Scenario C: New user */
              <VStack gap={3}>
                <Text fontSize="sm" color="fg.muted" textAlign="center">
                  {t('team.join.createAccountDescription')}
                </Text>

                <Stack as="form" onSubmit={handleSubmit(onSignupSubmit)} gap={3} width="full">
                  {/* Email (readonly) */}
                  <Field label={t('auth.email')}>
                    <Input
                      value={invitation.email}
                      type="email"
                      readOnly
                      variant="outline"
                      size="lg"
                      bg="gray.50"
                      color="fg.muted"
                    />
                  </Field>

                  {/* First name */}
                  <Field
                    label={t('team.join.firstNameLabel')}
                    errorText={formErrors.firstName?.message}
                  >
                    <Input
                      {...register('firstName')}
                      placeholder={t('team.join.firstNamePlaceholder')}
                      variant="outline"
                      size="lg"
                      autoComplete="given-name"
                    />
                  </Field>

                  {/* Last name */}
                  <Field
                    label={t('team.join.lastNameLabel')}
                    errorText={formErrors.lastName?.message}
                  >
                    <Input
                      {...register('lastName')}
                      placeholder={t('team.join.lastNamePlaceholder')}
                      variant="outline"
                      size="lg"
                      autoComplete="family-name"
                    />
                  </Field>

                  {/* Password */}
                  <Field
                    label={t('team.join.passwordLabel')}
                    errorText={formErrors.password?.message}
                  >
                    <Input
                      {...register('password')}
                      type="password"
                      placeholder={t('team.join.passwordPlaceholder')}
                      variant="outline"
                      size="lg"
                      autoComplete="new-password"
                    />
                  </Field>

                  <Button
                    type="submit"
                    colorPalette="gray"
                    size="lg"
                    width="full"
                    h={12}
                    loading={isSubmitting}
                    loadingText={t('team.join.signingUp')}
                  >
                    {t('team.join.signUpButton')}
                  </Button>
                </Stack>

                <OrSeparator />

                <ContinueWithGoogleButton
                  onClick={handleGoogleSignup}
                  isLoading={isGoogleLoading}
                />
              </VStack>
            )}
          </Stack>
        </Card.Body>
      </Card.Root>
    </PageLayout>
  );
};

/**
 * "or" divider between an alternative pair of actions
 */
const OrSeparator: React.FC = () => {
  const { t } = useTranslation();

  return (
    <HStack width="full" alignItems="center" gap={3} py={1}>
      <Separator flex="1" borderColor="gray.200" />
      <Text fontSize="xs" color="fg.muted" px={2} fontWeight="medium" whiteSpace="nowrap">
        {t('team.join.or')}
      </Text>
      <Separator flex="1" borderColor="gray.200" />
    </HStack>
  );
};

/**
 * Google entry point for the invitation, shared by the existing-account and
 * new-account states so both offer the same action.
 */
const ContinueWithGoogleButton: React.FC<{ onClick: () => void; isLoading: boolean }> = ({
  onClick,
  isLoading,
}) => {
  const { t } = useTranslation();

  return (
    <Button
      onClick={onClick}
      variant="outline"
      width="full"
      h={12}
      fontSize="16px"
      fontWeight="medium"
      loading={isLoading}
      loadingText={t('auth.redirectingToGoogle')}
      disabled={isLoading}
      gap={2}
      display="flex"
      alignItems="center"
    >
      {!isLoading ? <GoogleIcon /> : null}
      {t('team.join.continueWithGoogle')}
    </Button>
  );
};

/**
 * Shared page layout wrapper for the join team page
 */
const PageLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation();

  return (
    <Box color="dark" minHeight="100vh" bg="softCream" width="full">
      <Flex
        as="header"
        justify="space-between"
        align="center"
        px={{ base: 4, md: 8 }}
        py={4}
        position="absolute"
        top={0}
        left={0}
        right={0}
      >
        <Logo h="24px" />
        <Link
          href="mailto:contact@fluenceur.com"
          fontSize="sm"
          color="fg.muted"
          _hover={{ color: 'dark', textDecoration: 'underline' }}
        >
          {t('auth.needHelp')}
        </Link>
      </Flex>

      <Flex alignItems="center" justifyContent="center" minHeight="100vh" py={{ base: 12, md: 16 }}>
        <Container maxW="500px" width="full" color="dark">
          {children}
        </Container>
      </Flex>
    </Box>
  );
};

/**
 * Error card for displaying error states
 */
const ErrorCard: React.FC<{ message: string }> = ({ message }) => {
  const { t } = useTranslation();

  return (
    <Card.Root bg="white">
      <Card.Body>
        <VStack gap={4}>
          <Alert status="error">{message}</Alert>
          <Link
            href="/"
            fontSize="sm"
            color="fg.muted"
            _hover={{ color: 'dark', textDecoration: 'underline' }}
          >
            {t('team.join.returnToLogin')}
          </Link>
        </VStack>
      </Card.Body>
    </Card.Root>
  );
};

export default JoinTeamPage;
